import React, { useState, useEffect } from 'react';
import { FaTimes, FaSearch, FaMapMarkerAlt, FaSpinner } from 'react-icons/fa';
import { useCities } from '../hooks/useDataService';
import './CityModal.css';

const CityModal = ({ onClose, onCitySelect }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  
  const {
    cities,
    loading,
    error,
    searchCities,
    fetchCities,
    hasCities
  } = useCities({ autoLoad: true });

  // Busca com debounce
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (searchTerm.trim()) {
        setIsSearching(true);
        searchCities(searchTerm.trim()).finally(() => {
          setIsSearching(false);
        });
      } else {
        setIsSearching(true);
        fetchCities().finally(() => {
          setIsSearching(false);
        });
      }
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [searchTerm, searchCities, fetchCities]);

  const filteredCities = searchTerm
    ? cities.filter(city =>
        city.fullName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        city.name.toLowerCase().includes(searchTerm.toLowerCase())
      )
    : cities;

  const handleCityClick = (city) => {
    // Passa tanto o objeto completo quanto o nome formatado para compatibilidade
    onCitySelect(city.fullName || `${city.name}, ${city.state}`, city);
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  const isLoading = loading || isSearching;
  const showNoResults = !isLoading && searchTerm && filteredCities.length === 0;
  const showError = error && !hasCities;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content city-modal" onClick={(e) => e.stopPropagation()}>
        <button className="modal-close" onClick={onClose}>
          <FaTimes />
        </button>
        
        <div className="modal-header">
          <h2 className="modal-title">Selecione sua cidade</h2>
          <p className="modal-subtitle">
            Escolha a cidade onde você deseja receber a bateria
          </p>
          {showError && (
            <div className="error-message">
              <p>Erro ao carregar cidades. Usando dados locais.</p>
            </div>
          )}
        </div>

        <div className="search-container">
          <div className="search-input-wrapper">
            <FaSearch className="search-icon" />
            <input
              type="text"
              placeholder="Digite o nome da sua cidade..."
              value={searchTerm}
              onChange={handleSearchChange}
              className="search-input"
              disabled={isLoading && !searchTerm}
            />
            {isLoading && (
              <FaSpinner className="search-loading" />
            )}
          </div>
        </div>

        <div className="cities-list">
          {isLoading && !filteredCities.length ? (
            <div className="loading-cities">
              <FaSpinner className="loading-spinner" />
              <p>Carregando cidades...</p>
            </div>
          ) : filteredCities.length > 0 ? (
            filteredCities.map((city) => (
              <button
                key={city.id || city.name}
                className="city-item"
                onClick={() => handleCityClick(city)}
                disabled={isLoading}
              >
                <FaMapMarkerAlt className="city-icon" />
                <div className="city-info">
                  <span className="city-name">{city.name}</span>
                  <span className="city-state">{city.state}</span>
                </div>
                {city.coordinates?.lat && (
                  <span className="city-coordinates">📍</span>
                )}
              </button>
            ))
          ) : showNoResults ? (
            <div className="no-results">
              <p>Nenhuma cidade encontrada</p>
              <p className="no-results-subtitle">
                Tente digitar o nome da cidade de forma diferente
              </p>
            </div>
          ) : (
            <div className="empty-state">
              <p>Digite o nome da cidade para buscar</p>
            </div>
          )}
        </div>

        <div className="modal-footer">
          <button className="btn-secondary" onClick={onClose}>
            Cancelar
          </button>
        </div>
      </div>
    </div>
  );
};

export default CityModal;
